import { Helmet } from "react-helmet-async";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import parse from "html-react-parser";

const DEFAULT_METADATA = {
  title: "Benvora Bioscience - Advancing Healthcare Through Innovation",
  description: "Benvora Bioscience is a global leader in pharmaceutical innovation, committed to discovering and developing life-changing medicines for patients around the world.",
  keywords: "benvora bioscience, pharmaceutical, healthcare, innovation, medicines, drugs, research",
  robots: "index, follow",
  canonical: "https://benvorabioscience.com",
  author: "Benvora Bioscience Team",
  copyright: "Benvora Bioscience 2025",
  ogTitle: "Benvora Bioscience - Advancing Healthcare Through Innovation",
  ogDescription: "Benvora Bioscience is a global leader in pharmaceutical innovation, committed to discovering and developing life-changing medicines for patients around the world.",
  ogImage: "https://terrific-corgi-693.convex.cloud/api/storage/02db0418-d0d8-4e19-bf10-73e64563fbaa",
  ogUrl: "https://benvorabioscience.com",
  ogType: "website",
  twitterCard: "summary_large_image",
  twitterTitle: "Benvora Bioscience - Advancing Healthcare Through Innovation",
  twitterDescription: "Benvora Bioscience is a global leader in pharmaceutical innovation, committed to discovering and developing life-changing medicines for patients around the world.",
  twitterImage: "https://terrific-corgi-693.convex.cloud/api/storage/02db0418-d0d8-4e19-bf10-73e64563fbaa",
  favicon: "https://terrific-corgi-693.convex.cloud/api/storage/02db0418-d0d8-4e19-bf10-73e64563fbaa",
  hreflang: "en-US",
  geoRegion: "IN-HR",
  geoPlacename: "Panchkula, Haryana",
  geoPosition: "30.6942;76.8606",
};

export function usePageMetadata(pagePath: string) {
  // Bypass type inference by using dynamic property access
  const serverMetadata = useQuery((api as any).pageMetadata.getPageMetadata, { pagePath });

  // Always use default metadata during SSR and initial render to prevent hydration mismatch
  const finalMetadata = (!serverMetadata) ? {
    ...DEFAULT_METADATA,
    canonical: `https://benvorabioscience.com${pagePath}`,
    ogUrl: `https://benvorabioscience.com${pagePath}`,
    isRawMode: false,
    customMetaHtml: "",
  } : {
    ...serverMetadata,
    robots: serverMetadata.robots || DEFAULT_METADATA.robots,
    author: serverMetadata.author || DEFAULT_METADATA.author,
    copyright: serverMetadata.copyright || DEFAULT_METADATA.copyright,
    ogType: serverMetadata.ogType || DEFAULT_METADATA.ogType,
    twitterCard: serverMetadata.twitterCard || DEFAULT_METADATA.twitterCard,
    hreflang: serverMetadata.hreflang || DEFAULT_METADATA.hreflang,
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Benvora Bioscience",
    "url": finalMetadata.canonical || "https://benvorabioscience.com",
    "logo": finalMetadata.ogImage || "",
    "description": finalMetadata.description || "",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-7009223262",
      "contactType": "customer service",
      "email": "info@benvorabioscience.com"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Plot No.367, Abhey Pur, Industrial Area Phase-1",
      "addressLocality": "Panchkula",
      "addressRegion": "Haryana",
      "postalCode": "134109",
      "addressCountry": "IN"
    }
  };

  // If Raw Mode is enabled, only render the custom HTML
  if (finalMetadata.isRawMode && finalMetadata.customMetaHtml) {
    return {
      metadata: finalMetadata,
      HelmetTags: () => (
        <Helmet>
          {typeof finalMetadata.customMetaHtml === 'string' && parse(finalMetadata.customMetaHtml)}
        </Helmet>
      )
    };
  }

  return {
    metadata: finalMetadata,
    HelmetTags: () => (
      <Helmet>
        {finalMetadata.title && <title>{finalMetadata.title}</title>}
        {finalMetadata.title && <meta name="title" content={finalMetadata.title} />}
        {finalMetadata.description && <meta name="description" content={finalMetadata.description} />}
        {finalMetadata.keywords && <meta name="keywords" content={finalMetadata.keywords} />}
        {finalMetadata.robots && <meta name="robots" content={finalMetadata.robots} />}
        {finalMetadata.author && <meta name="author" content={finalMetadata.author} />}
        {finalMetadata.copyright && <meta name="copyright" content={finalMetadata.copyright} />}

        {finalMetadata.canonical && <link rel="canonical" href={finalMetadata.canonical} />}

        {finalMetadata.ogType && <meta property="og:type" content={finalMetadata.ogType} />}
        {finalMetadata.ogUrl && <meta property="og:url" content={finalMetadata.ogUrl} />}
        {finalMetadata.ogTitle && <meta property="og:title" content={finalMetadata.ogTitle} />}
        {finalMetadata.ogDescription && <meta property="og:description" content={finalMetadata.ogDescription} />}
        {finalMetadata.ogImage && <meta property="og:image" content={finalMetadata.ogImage} />}

        {finalMetadata.twitterCard && <meta name="twitter:card" content={finalMetadata.twitterCard} />}
        {finalMetadata.twitterTitle && <meta name="twitter:title" content={finalMetadata.twitterTitle} />}
        {finalMetadata.twitterDescription && <meta name="twitter:description" content={finalMetadata.twitterDescription} />}
        {finalMetadata.twitterImage && <meta name="twitter:image" content={finalMetadata.twitterImage} />}

        {finalMetadata.hreflang && finalMetadata.canonical && (
          <link rel="alternate" hrefLang={finalMetadata.hreflang} href={finalMetadata.canonical} />
        )}

        {finalMetadata.geoRegion && <meta name="geo.region" content={finalMetadata.geoRegion} />}
        {finalMetadata.geoPlacename && <meta name="geo.placename" content={finalMetadata.geoPlacename} />}
        {finalMetadata.geoPosition && <meta name="geo.position" content={finalMetadata.geoPosition} />}

        {finalMetadata.favicon && <link rel="icon" type="image/png" href={finalMetadata.favicon} />}

        {(finalMetadata.description || finalMetadata.ogImage) && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
        )}
        
        {/* Render custom HTML metadata if present */}
        {finalMetadata.customMetaHtml && typeof finalMetadata.customMetaHtml === 'string' && parse(finalMetadata.customMetaHtml)}
      </Helmet>
    )
  };
}